import {
  DragSourceMonitor,
  DropTargetMonitor,
  useDrag,
  useDrop,
} from 'react-dnd';
import clsx from 'clsx';
import { classes } from 'app/chakra-theme/components/TableList';
import { Row, RowRendererProps } from 'react-data-grid';
import { useState } from 'react';

import { showRowTopShadow } from './utils';

interface DragAndDropItem {
  index: number;
}

export interface DraggableRowRenderProps<R, SR>
  extends RowRendererProps<R, SR> {
  onRowReorder: (
    sourceIndex: number,
    targetIndex: number,
    isTop: boolean
  ) => void;
  onHover?: (index: number) => void;
}

export function DraggableListRow<R, SR>({
  rowIdx,
  isRowSelected,
  className,
  onRowReorder,
  onHover,
  ...props
}: DraggableRowRenderProps<R, SR>) {
  let divRef: HTMLDivElement | null;
  const [isTop, setIsTop] = useState(false);

  const [{ isDragging }, drag] = useDrag({
    type: 'ROW_DRAG',
    item: { index: rowIdx },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: 'ROW_DRAG',
    drop({ index }: DragAndDropItem) {
      onRowReorder(index, rowIdx, isTop);
    },
    hover: (item: DragAndDropItem, monitor: DropTargetMonitor) => {
      if (onHover) {
        onHover(item.index);
      }

      setIsTop(showRowTopShadow(divRef, monitor));
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const classNameExtended = clsx(className, classes.rowDraggable, {
    [classes.rowDragging]: isDragging,
    [classes.rowHover]: isOver,
    [classes.rowOver]: isOver && !isTop,
    [classes.rowOverInset]: isOver && isTop,
  });

  return (
    <Row
      ref={(ref) => {
        if (ref) {
          divRef = ref;
        }
        drag(drop(ref));
      }}
      rowIdx={rowIdx}
      isRowSelected={isRowSelected}
      className={classNameExtended}
      onMouseDown={(e) => e.preventDefault()}
      {...props}
    />
  );
}
